import { useEffect, useMemo, useState } from 'react'

import { studentController } from '@/controller/student.controller'
import type { Student, StudentGroups } from '@/dto'

type UseStudentItemsOptions = {
  studentStatus?: ('ACTIVE' | 'INACTIVE')[]
}

type UseStudentItemsReturn = {
  /** 학생 목록 */
  students: Student[]
  /** 학생 그룹 데이터 (lessonClassColl 등 포함) */
  studentGroups: StudentGroups | null
  /** 로딩 중 여부 */
  isLoading: boolean
  /** 학생 -> 반 매핑 (학생이 여러 반에 속할 수 있음) */
  studentLessonClassMap: Map<Student['id'], number[]>
  /** 반 ID 정렬 순서 (GET /lesson-classes index ASC 순서) */
  lessonClassOrder: number[]
  /** 반 이름 조회 함수 */
  getLessonClassName: (classId: number | string) => string
}

/**
 * 학생 데이터 fetch 훅
 * - StudentGroups 전체를 fetch하여 학생 목록, 반 매핑 등 제공
 */
export const useStudentItems = (options?: UseStudentItemsOptions): UseStudentItemsReturn => {
  const [studentGroups, setStudentGroups] = useState<StudentGroups | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)
    studentController
      .fetchStudentGroups(options)
      .then(setStudentGroups)
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 학생 목록
  const students = useMemo(() => {
    if (!studentGroups?.studentColl) return []
    return [...studentGroups.studentColl.values()]
  }, [studentGroups])

  // 학생 -> 반 매핑 (한 학생이 여러 반에 속할 수 있으므로 number[] 사용)
  const studentLessonClassMap = useMemo(() => {
    if (!studentGroups?.lessonClassColl) return new Map<Student['id'], number[]>()
    const map = new Map<Student['id'], number[]>()
    for (const lessonClass of studentGroups.lessonClassColl.toArr) {
      for (const member of lessonClass.memberList) {
        const classIds = map.get(member.id)
        if (classIds) {
          classIds.push(lessonClass.id)
        } else {
          map.set(member.id, [lessonClass.id])
        }
      }
    }
    return map
  }, [studentGroups])
  // 반 ID 정렬 순서 (lessonClassColl은 GET /lesson-classes 순서)
  const lessonClassOrder = useMemo(() => {
    if (!studentGroups?.lessonClassColl) return []
    return studentGroups.lessonClassColl.toArr.map(({ id }) => id)
  }, [studentGroups])

  // 반 이름 조회 함수
  const getLessonClassName = useMemo(() => {
    return (classId: number | string) => {
      const lessonClass = studentGroups?.lessonClassColl?.get(Number(classId))
      return lessonClass?.name ?? ''
    }
  }, [studentGroups])

  return {
    students,
    studentGroups,
    isLoading,
    studentLessonClassMap,
    lessonClassOrder,
    getLessonClassName,
  }
}

export default useStudentItems
